import React from 'react';

import css from './SectionNextmineTerms.module.css';

// Nextmine - Terms of Service, formateret læsbart.
const SectionNextmineTerms = () => {
  return (
    <div className={css.root}>
      <div className={css.eyebrow}>Nextmine</div>
      <h1 className={css.title}>Terms of Service</h1>
      <div className={css.meta}>Last updated / effective date: 15 July 2026</div>

      <div className={css.summary}>
        <p className={css.summaryTitle}>At a glance</p>
        <ul>
          <li>Nextmine is a marketplace. We connect buyers and sellers - we are not a party to the sale and we don’t own, inspect or authenticate any item.</li>
          <li>Every payment is held securely by our payment processor (Stripe) and paid out to the seller on a fixed schedule - 14 days after you confirm receipt, or 30 days after the order is marked shipped if you don’t.</li>
          <li>Sellers must verify their identity and may only list items they lawfully own and describe accurately.</li>
          <li>No change-of-mind returns. If an item isn’t received or is materially not as described, raise an issue <strong>directly with the seller within 48 hours</strong> of receiving it - from <strong>Orders &amp; sales → Raise an issue</strong>.</li>
          <li>This is a plain-language summary only - the full terms below apply.</li>
        </ul>
      </div>
      <hr className={css.hairline} />

      <p className={css.p}>Please read these Terms of Service carefully before using our Service.</p>

      <h2 className={css.h2}>1 · Interpretation and definitions</h2>
      <h3 className={css.h3}>1.1 Interpretation</h3>
      <p className={css.p}>
        The words whose initial letter is capitalised have meanings defined under the following
        conditions. The definitions have the same meaning whether they appear in the singular or the
        plural.
      </p>
      <h3 className={css.h3}>1.2 Definitions</h3>
      <p className={css.p}>For the purposes of these Terms of Service:</p>
      <ul className={css.list}>
        <li><span className={css.term}>Account</span> - a unique account created for you to access our Service.</li>
        <li><span className={css.term}>Affiliate</span> - an entity that controls, is controlled by, or is under common control with a party (50%+ ownership).</li>
        <li><span className={css.term}>Application</span> - the software program provided by the Company, named Nextmine.</li>
        <li><span className={css.term}>Buyer</span> - a user placing orders for Goods.</li>
        <li><span className={css.term}>Country</span> - Denmark.</li>
        <li><span className={css.term}>Company</span> (“the Company”, “the Operator”, “We”, “Us”, “Our”) - OMJ, Poul Anker Bechs Vej 302, 9200 Aalborg SV, Denmark, CVR 30860403.</li>
        <li><span className={css.term}>Content</span> - text, images or other information you post, upload or make available.</li>
        <li><span className={css.term}>Device</span> - any device that can access the Service.</li>
        <li><span className={css.term}>Feedback</span> - feedback, innovations or suggestions you send about our Service.</li>
        <li><span className={css.term}>Good</span> - the items (jewelry and related items) offered for sale or trade on the Service.</li>
        <li><span className={css.term}>Order</span> - a request to purchase or trade Goods.</li>
        <li><span className={css.term}>Payment Processor</span> - the third-party payment infrastructure used (Sharetribe and its underlying provider, Stripe), each under its own terms.</li>
        <li><span className={css.term}>Seller</span> - a user listing Goods for trade.</li>
        <li><span className={css.term}>Service</span> - the Application, the Website, or both.</li>
        <li><span className={css.term}>Terms</span> - these Terms of Service, the entire agreement between you and the Company.</li>
        <li><span className={css.term}>Third-party Social Media Service</span> - services or content provided by a third party that may be made available by the Service.</li>
        <li><span className={css.term}>Website</span> - Nextmine, accessible at nextminejewelry.com.</li>
        <li><span className={css.term}>You</span> - the individual or legal entity accessing or using the Service.</li>
      </ul>

      <h2 className={css.h2}>2 · Contact us</h2>
      <p className={css.contact}>
        If you have any questions about these Terms, you can contact us by email at{' '}
        <a href="mailto:hi@nextminejewelry.com">hi@nextminejewelry.com</a>, or by visiting nextminejewelry.com.
      </p>

      <h2 className={css.h2}>3 · Acknowledgment</h2>
      <p className={css.p}>
        These Terms govern the use of the Service and form the agreement between you and the Company.
        Your access to and use of the Service is conditioned on your acceptance of and compliance with
        these Terms. They apply to all visitors and users. By accessing or using the Service you agree
        to be bound by them; if you disagree with any part, you may not access the Service.
      </p>
      <p className={css.p}>
        You represent that you are over the age of majority according to the laws of your country or
        of Denmark, whichever is higher. The Company does not permit those under that age to use the
        Service. Your use is also conditioned on acceptance of our Privacy Policy.
      </p>

      <h2 className={css.h2}>4 · The nature of our service</h2>
      <p className={css.p}>
        Nextmine is an online marketplace that allows sellers to list Goods and buyers to purchase
        them. Buyers and sellers transact directly with one another. We are a facilitator and are not
        a party to any sale concluded between a buyer and a seller.
      </p>
      <p className={css.p}>
        We do not own, take possession of, inspect, appraise, authenticate, certify or guarantee any
        Good. Any description, attribution, valuation, materials, age, origin or condition statement is
        made solely by the seller, who is solely responsible for its accuracy. Any reference in our
        marketing to curation or selection describes editorial selection only and is not an appraisal,
        authentication or warranty.
      </p>

      <h2 className={css.h2}>5 · Governing law</h2>
      <p className={css.p}>
        The laws of Denmark, excluding its conflict-of-law rules, govern these Terms and your use of
        the Service. Your use may also be subject to other local, national or international laws.
      </p>
      <h3 className={css.h3}>5.1 United States legal compliance</h3>
      <p className={css.p}>
        You represent that you are not located in a country subject to a U.S. government embargo or
        designated as “terrorist supporting”, and that you are not on any U.S. government list of
        prohibited or restricted parties.
      </p>
      <h3 className={css.h3}>5.2 Severability</h3>
      <p className={css.p}>
        If any provision is held unenforceable, it will be changed and interpreted to accomplish its
        objectives to the greatest extent possible, and the remaining provisions continue in full
        force.
      </p>
      <h3 className={css.h3}>5.3 Waiver</h3>
      <p className={css.p}>
        Failure to exercise a right or require performance of an obligation does not affect a party’s
        ability to do so later, nor does waiver of a breach waive any subsequent breach.
      </p>

      <h2 className={css.h2}>6 · User accounts</h2>
      <h3 className={css.h3}>6.1 Account creation</h3>
      <p className={css.p}>
        You must provide information that is accurate, complete and current. Failure to do so is a
        breach that may result in termination. You may not use a username that belongs to another
        person, is unlawful, infringes another’s rights, or is offensive.
      </p>
      <h3 className={css.h3}>6.2 Account information</h3>
      <p className={css.p}>
        You may be asked to supply your name, email, phone number and address, and documents for
        identity verification. Before or during listing Goods, you may be asked for bank account
        details and identity documents (as required by our Payment Processor and Section 8). Before or
        during an Order, you may be asked for payment card details, billing address and shipping
        information.
      </p>
      <h3 className={css.h3}>6.3 Account review</h3>
      <p className={css.p}>
        Unless part of a feature of the Service, we do not perform background checks or endorse users,
        and accept no responsibility for the accuracy of information provided by users.
      </p>
      <h3 className={css.h3}>6.4 Account password</h3>
      <p className={css.p}>
        You are responsible for safeguarding your password and for all activity under it. Do not
        disclose it to any third party, and notify us immediately of any unauthorized use.
      </p>
      <h3 className={css.h3}>6.5 Account termination</h3>
      <p className={css.p}>
        We may suspend or terminate your account immediately, without notice, including if you breach
        these Terms. Termination does not affect Orders already in progress, which are completed or
        refunded under Section 9. You may stop using the Service or delete your account at any time.
      </p>

      <h2 className={css.h2}>7 · Content</h2>
      <h3 className={css.h3}>7.1 Your right to post content</h3>
      <p className={css.p}>
        You are responsible for the Content you post, including its legality and appropriateness. By
        posting Content you grant us the right and licence to use, modify, display, reproduce and
        distribute it on the Service. You retain your rights to your Content and warrant that it is
        yours to post and does not violate others’ rights.
      </p>
      <h3 className={css.h3}>7.2 Listing review</h3>
      <p className={css.p}>
        We may, at our discretion and as optional quality control, review, approve, decline, reformat
        or remove any listing or Content. Any such review is discretionary and is not a warranty,
        endorsement, appraisal or authentication that a Good is accurate, authentic, lawful or as
        described. We assume no liability arising from the review, approval or non-removal of a
        listing.
      </p>
      <h3 className={css.h3}>7.3 Content restrictions</h3>
      <p className={css.p}>You are solely responsible for all activity under your account. You may not transmit Content that is:</p>
      <ul className={css.list}>
        <li>Unlawful or promoting unlawful activity.</li>
        <li>Defamatory, discriminatory or mean-spirited.</li>
        <li>Spam, unsolicited advertising, chain letters, lottery or gambling.</li>
        <li>Containing viruses, malware or harmful code.</li>
        <li>Infringing any proprietary rights.</li>
        <li>Impersonating any person or entity.</li>
        <li>Violating the privacy of any third person.</li>
        <li>False or misleading.</li>
      </ul>
      <p className={css.p}>
        We reserve the right, but not the obligation, to determine whether Content is appropriate and
        to remove it. You use the Service at your own risk.
      </p>
      <h3 className={css.h3}>7.4 Content backups</h3>
      <p className={css.p}>
        Although regular backups are performed, we do not guarantee against loss or corruption of
        data. Keep an independent copy of your Content.
      </p>
      <h3 className={css.h3}>7.5 Copyright infringement (DMCA)</h3>
      <p className={css.p}>
        We respect intellectual property rights. If you believe your copyrighted work has been
        infringed, send a written notice to <a href="mailto:hi@nextminejewelry.com">hi@nextminejewelry.com</a>{' '}
        including: your signature; a description of the work; the location of the infringing material;
        your contact details; a good-faith statement; and a statement under penalty of perjury that the
        information is accurate and you are authorised to act.
      </p>

      <h2 className={css.h2}>8 · Seller obligations</h2>
      <p className={css.p}>By listing a Good, you (as seller) represent and warrant that:</p>
      <ul className={css.list}>
        <li>You lawfully own or are authorised to sell the Good, free of any undisclosed lien or third-party claim.</li>
        <li>The Good is not stolen, counterfeit or a misrepresentation, and infringes no rights.</li>
        <li>Your description - materials, gemstones, metal, hallmarks, weight, age, origin, maker, condition - is accurate and not misleading.</li>
        <li>The sale, export and shipment comply with all applicable laws (precious metals and stones, cultural property, endangered-species materials such as ivory or certain corals (CITES), and sanctions/export controls).</li>
        <li>You will ship promptly, in the condition described, fully insured and with tracking (see 8.2).</li>
      </ul>
      <p className={css.p}>
        You, not the Company, are responsible for any taxes, duties and legal obligations arising from
        your sale, except where these Terms or law expressly place an obligation on the Company.
      </p>
      <h3 className={css.h3}>8.1 High-volume sellers (U.S. INFORM Consumers Act)</h3>
      <p className={css.p}>
        Sellers who reach a high volume of transactions must, upon request, provide and verify bank
        account information, contact information and tax identification number. We may collect, verify
        and - where required by law - disclose certain seller identity and contact information to
        buyers. Failure to provide required information may result in suspension.
      </p>
      <h3 className={css.h3}>8.2 Shipping, tracking and insurance</h3>
      <p className={css.p}>
        You must ship every Good <span className={css.term}>fully insured and with tracking</span>,
        and provide a valid tracking number when marking the item as shipped. You choose the insured
        value, but it is your responsibility to insure the shipment for at least the sale price.
      </p>
      <p className={css.p}>
        <span className={css.term}>Risk of loss and damage in transit rests with the seller</span>{' '}
        until the buyer receives the Good. If a package is lost, stolen or damaged in the mail, the
        loss is yours to bear and to recover from your carrier or shipping insurer. The Company is not
        a shipper or insurer, does not carry insurance on your behalf, and cannot and will not
        reimburse you for items lost, stolen or damaged in transit. Where an item is not received or
        arrives damaged, the buyer’s remedy is the dispute process in Section 9.9, which may result in
        a refund funded from amounts otherwise payable to you.
      </p>

      <h2 className={css.h2}>9 · Orders, payments and disputes</h2>
      <p className={css.p}>By placing an Order you warrant that you are legally capable of entering into binding contracts.</p>
      <h3 className={css.h3}>9.1 Our position in orders</h3>
      <p className={css.p}>
        We are a facilitator between buyers and sellers and a third party to Orders, which limits our
        liability in any dispute between them. We are not a party to any agreement you enter with
        another user.
      </p>
      <h3 className={css.h3}>9.2 Payment and payout schedule</h3>
      <p className={css.p}>
        All payments are processed through our Payment Processor (Sharetribe / Stripe), under its own
        terms. We do not hold or control user funds; funds are collected from the buyer at purchase
        and held by the Payment Processor - not by us - until released.
      </p>
      <p className={css.p}>
        Payout to the seller follows a fixed schedule. Funds are released to the seller{' '}
        <span className={css.term}>14 days after the buyer confirms receipt</span> of the item. If the
        buyer does not confirm receipt, funds are released automatically{' '}
        <span className={css.term}>30 days after the seller marks the order as shipped</span>. The
        buyer cannot accelerate payout. This is a payout schedule for sellers; it is not a buyer
        guarantee, and we do not hold funds in trust.
      </p>
      <p className={css.p}>
        Sellers must provide a valid tracking number when marking an item as shipped.
      </p>
      <h3 className={css.h3}>9.3 Service fees</h3>
      <p className={css.p}>
        We charge fees (and applicable taxes) for use of the Service:
      </p>
      <ul className={css.list}>
        <li><span className={css.term}>Buyer fee:</span> a 6% fee is added on top of the item price at checkout.</li>
        <li><span className={css.term}>Seller commission:</span> an 18% commission is deducted from the sale price paid out to the seller.</li>
      </ul>
      <p className={css.p}>
        The exact amounts are displayed during your Order. We reserve the right to change fees, with
        changes applying to Orders placed after the change.
      </p>
      <h3 className={css.h3}>9.4 Taxes and marketplace facilitator obligations</h3>
      <p className={css.p}>
        Prices may exclude taxes. Where required by law (including as a “marketplace facilitator” under
        applicable U.S. state law), we will calculate and collect sales tax, VAT, GST or similar at
        checkout and remit them. Otherwise, the seller and/or buyer is responsible for applicable
        taxes.
      </p>
      <h3 className={css.h3}>9.5 Customs, import duties and cross-border shipments</h3>
      <p className={css.p}>
        Goods may ship internationally. Unless stated otherwise at checkout, the buyer is the importer
        of record and is responsible for all import duties, VAT, customs charges and brokerage fees.
        Risk of loss or damage in transit is governed by Section 8.2 and rests with the seller until
        the buyer receives the Good.
      </p>
      <h3 className={css.h3}>9.6 Availability, errors and inaccuracies</h3>
      <p className={css.p}>
        Goods may be mispriced, described inaccurately or unavailable. We do not guarantee the accuracy
        of any information and may correct errors and update information at any time without notice.
      </p>
      <h3 className={css.h3}>9.7 Order cancellation</h3>
      <p className={css.p}>
        <span className={css.term}>By us:</span> we may refuse or cancel an Order at any time (e.g.
        availability, errors in description or price, suspected fraud).
      </p>
      <p className={css.p}>
        <span className={css.term}>By sellers:</span> if a seller cancels before funds are released,
        the amount paid by the buyer (including refundable Service fees) is refunded to the buyer.
      </p>
      <p className={css.p}>
        <span className={css.term}>By buyers:</span> a buyer may cancel only before the seller has
        shipped, subject to the rules shown at checkout. Once shipped, an Order may not be cancelled
        for change of mind; the only remedy is the dispute process in 9.9.
      </p>
      <h3 className={css.h3}>9.8 Returns and final sales</h3>
      <p className={css.p}>
        Goods may not be returned or exchanged for change of mind or subjective dissatisfaction. The
        Service does not operate a returns facility and does not fund change-of-mind returns. This does
        not exclude a buyer’s remedies where a Good is not received or is materially not as described
        (Section 9.9), nor any non-excludable right under the mandatory law of the buyer’s own
        jurisdiction.
      </p>
      <h3 className={css.h3}>9.9 Order disputes</h3>
      <p className={css.p}>
        If a buyer is dissatisfied - for example, the item was not received, or is materially not as
        described - they must <span className={css.term}>raise an issue directly with the seller within
        48 hours of receiving the item</span>. To do this, open the order under{' '}
        <span className={css.term}>Orders &amp; sales</span> in your account and choose{' '}
        <span className={css.term}>Raise an issue</span>. This shares the seller’s contact details with
        you so the two of you can resolve the matter directly between yourselves. If it is not resolved
        within <span className={css.term}>7 days</span> of first contact, we will review the case and
        may, at our reasonable discretion, facilitate a refund, partial refund or release of funds.
        While a dispute is open, payout is paused. Our decision relates only to the timing and release
        of funds held by the Payment Processor and does not determine the underlying legal rights
        between buyer and seller.
      </p>
      <p className={css.p}>
        <span className={css.term}>Deadline where an item never arrives.</span> If your order does not
        arrive, you must raise an issue before the order completes automatically - which happens{' '}
        <span className={css.term}>30 days after the seller marks the order as shipped</span>. We send
        reminders before this deadline. Once an order has completed, it can no longer be disputed and no
        refund can be issued through the Service, so if your order has not arrived, raise an issue
        before this deadline.
      </p>
      <h3 className={css.h3}>9.10 Returns as part of a dispute resolution</h3>
      <p className={css.p}>
        Where a dispute is resolved in the buyer’s favor on the condition that the Good is returned to
        the seller, the buyer must return it promptly, insured and with tracking, and submit the return
        tracking number on the order page.{' '}
        <span className={css.term}>The buyer pays for return shipping.</span> Because a return only
        arises from a dispute (not change of mind), this cost is not reimbursed by the Company. The
        buyer’s refund for the item is released once the seller confirms the returned Good has been
        received, or automatically if the seller does not confirm within 10 days of the return being
        shipped. If the buyer does not ship the return within 14 days, the order is completed and the
        seller is paid. If the returned Good arrives damaged, altered or not the item that was sold, the
        seller may reopen the dispute for our review.
      </p>
      <p className={css.p}>
        Card chargebacks are handled separately under the card networks’ rules and fall outside this
        payout schedule.
      </p>

      <h2 className={css.h2}>10 · Disclaimer and limitation of liability</h2>
      <h3 className={css.h3}>10.1 Limitation of liability</h3>
      <p className={css.p}>
        The entire liability of the Company and its suppliers, and your exclusive remedy, is limited to
        the amount actually paid by you through the Service, or USD 100 (or local-currency equivalent)
        if you haven’t purchased anything. To the maximum extent permitted by law, we are not liable
        for any special, incidental, indirect or consequential damages. Some jurisdictions do not allow
        such limitations, so some may not apply to you.
      </p>
      <h3 className={css.h3}>10.2 “As is” and “as available”</h3>
      <p className={css.p}>
        The Service is provided “as is” and “as available”, with all faults and without warranty of any
        kind. We disclaim all warranties, express or implied, including merchantability, fitness for a
        particular purpose, title and non-infringement, and make no representation that the Service will
        be uninterrupted or error-free, or that Goods listed by sellers are authentic or as described.
      </p>
      <h3 className={css.h3}>10.3 Links to other websites</h3>
      <p className={css.p}>
        The Service may link to third-party sites we do not control and for which we assume no
        responsibility. Read their terms and privacy policies.
      </p>
      <h3 className={css.h3}>10.4 Translation</h3>
      <p className={css.p}>
        If these Terms have been translated, the original English text prevails in case of a dispute.
      </p>

      <h2 className={css.h2}>11 · Complaints and disputes with us</h2>
      <h3 className={css.h3}>11.1 How to raise a complaint</h3>
      <p className={css.p}>
        Email <a href="mailto:hi@nextminejewelry.com">hi@nextminejewelry.com</a> with your name and account email, the
        relevant Order number, a description of the issue and the outcome you seek. We aim to
        acknowledge within 15 business days and provide a substantive response within 30 days.
      </p>
      <h3 className={css.h3}>11.2 Which process applies</h3>
      <ul className={css.list}>
        <li>A complaint about an Order with another user is handled under Section 9.9.</li>
        <li>A complaint about the Service or the Company is handled under this Section 11.</li>
      </ul>
      <h3 className={css.h3}>11.3 Informal resolution first</h3>
      <p className={css.p}>
        You agree to first try to resolve any concern informally by contacting us before pursuing any
        other remedy.
      </p>

      <h2 className={css.h2}>12 · Intellectual property of the service</h2>
      <p className={css.p}>
        The Service and its original content (excluding user Content), features and functionality are
        the exclusive property of the Company and its licensors, protected by copyright and trademark
        laws. Our trademarks may not be used without our prior written consent.
      </p>

      <h2 className={css.h2}>13 · Your feedback to us</h2>
      <p className={css.p}>
        You assign all rights in any Feedback you provide. If that assignment is ineffective, you grant
        the Company a non-exclusive, perpetual, irrevocable, royalty-free, worldwide licence to use and
        exploit such Feedback without restriction.
      </p>

      <h2 className={css.h2}>14 · Changes to these terms</h2>
      <p className={css.p}>
        We may modify or replace these Terms at any time. If a revision is material, we will make
        reasonable efforts to give at least 30 days’ notice before the new terms take effect. By
        continuing to use the Service after revisions become effective, you agree to the revised terms.
        If you do not agree, please stop using the Service.
      </p>

      <hr className={css.hairline} />
      <p className={css.contact} style={{ marginTop: '18px' }}>
        Questions? Contact us at <a href="mailto:hi@nextminejewelry.com">hi@nextminejewelry.com</a>.
      </p>
    </div>
  );
};

export default SectionNextmineTerms;
